import {
  UserProps,
  useUser,
} from "@customer-tools-web/component-authentication";
import { I18NProps, useI18N } from "@customer-tools-web/component-i18n";
import {
  IconTypes,
  isDesktop,
  isMobile,
  isTablet,
  media,
  Position,
  StaticTooltip,
  theme,
} from "@customer-tools-web/component-ui";
import {
  MarketConfig,
  useMarketConfig,
} from "@customer-tools-web/market-extension-resolver";
import { LocationDescriptorObject } from "history";
import { compact, debounce, find } from "lodash";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled, {
  createGlobalStyle,
  css,
  ThemeProvider,
} from "styled-components";
import * as moment from "moment";

import {
  FeatureKey,
  ProductKey,
  useAgentEntitlement,
} from "./clients/get-entitlement";

import logo from "../../assets/logo-3.malaysia.svg";
import mobileLogo from "../../assets/logo-2.mobile.malaysia.svg";
import { usePlatform } from "../hooks";
import { InboxStatistic, MarketWatchStatistic } from "../index";

import LoginAsAgentHint from "./components/LoginAsAgentHint";
import { Menu_Keys_Type } from "./components/Menus";
import {
  LinkButton,
  MenuTag,
  TooltipContentWrapper,
  LabelWrap,
  BetaTag,
  CounterTag,
  NotificationBadge,
} from "./components/NavigatorLink";
import AccountMenu from "./menu/AccountMenu";
import NavigationMenu from "./menu/NavigationMenu";
import { generateLogoutUrl, setLocalStorageItem } from "./components/tool";
import SpecialistSelfServeOnboardingTooltip, {
  Props as SpecialistSelfServeProps,
} from "./components/SpecialistSelfServeOnboardingTooltip";
import { useHistory, useLocation } from "react-router";

const BRICKZ_ONBOARDING_TOOLTIP_STORAGE_KEY = "x-brickz-onboarding-tooltip";

export interface SubMenuWithFormItemType {
  env: string | undefined;
  link: string;
  query: any;
  tokenName: string;
  trackMenuTap: TrackMenuType | undefined;
}

export interface MenuItemType {
  key: Menu_Keys_Type;
  label?: string | React.ReactNode;
  icon?: IconTypes;
  iconSize?: string;
  iconMarginRight?: string;
  needOpenIcon?: boolean;
  link?: string;
  alternateLink?: string[];
  click?: () => void;
  disabled?: boolean;
  getCustomNode?: (item: MenuItemType) => React.ReactNode;
  dataTest?: string;
  shouldUseRouter?: boolean;
  routerOptions?: LocationDescriptorObject;
  hiddenOnMobile?: boolean;
  subMenus?: Array<MenuItemType | null>;
  subMenusWithForm?: any;
  getCustomTagNode?: React.ReactNode;
  tooltipPanelLabel?: (
    isDrawerOpen: boolean,
    onConfirm?: any
  ) => React.ReactNode;
  openInSameTab?: boolean;
  staticTooltip?: React.ReactNode;
  onTitleClick?: (e: any) => void;
}

export interface NotificationType {
  id: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  expiresAt?: string;
  updated?: string;
  extra?: { type: string; ippProDisplay: string; id: string };
  viewed?: boolean;
  clicked?: boolean;
  linkText?: string;
  url?: string;
}

export type TrackMenuType = (
  menuName: MenuItemType["key"],
  fromLink?: string
) => void;

const canViewSubscriptionsMenu = (
  ctServer: string,
  shouldUseRouter: boolean,
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  return user.permissions.viewSubscriptionsMenu
    ? {
        key: "subscriptions",
        icon: "packages",
        label: t("account.subscriptions"),
        shouldUseRouter,
        link: ctServer + "/plans/subscription",
        alternateLink: [ctServer + "/plans/credit-usage"],
        dataTest: "subscriptions",
      }
    : null;
};

const canViewPaymentHistory = (
  ctServer: string,
  shouldUseRouter: boolean,
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  return process.env.REACT_APP_FEATURE_PAYMENT_GATEWAY === "on" &&
    user.permissions.isAgent
    ? {
        key: "payment-history",
        icon: "payment-history",
        iconSize: "1.9rem",
        iconMarginRight: "1.2rem",
        label: t("account.paymentHistory"),
        shouldUseRouter,
        link: ctServer + "/payment-history",
        alternateLink: [ctServer + "/payment-history"],
        dataTest: "payment-history",
      }
    : null;
};

const canViewMyTeam = (
  umServer: string,
  shouldUseRouter: boolean,
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  return user.permissions.viewMyTeamMenu
    ? {
        key: "team",
        icon: "myTeam",
        shouldUseRouter,
        label: t("account.team"),
        link: umServer + "/users",
        dataTest: "my-team",
      }
    : null;
};

const canViewEnquirySettings = (
  ctServer: string,
  shouldUseRouter: boolean,
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  return user.permissions.editOrganisationEnquiries
    ? {
        key: "enquiry",
        icon: "enquirySettings",
        label: t("account.enquiry"),
        shouldUseRouter,
        link: ctServer + "/enquiry-settings",
        dataTest: "enquiry",
      }
    : null;
};

const canViewAllocation = (
  ctServer: string,
  shouldUseRouter: boolean,
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  return user.permissions.allocation.canViewAllocationMenu
    ? {
        key: "allocations",
        icon: "allocation",
        label: t("account.allocations"),
        shouldUseRouter,
        link: ctServer + "/allocations",
        dataTest: "allocations",
      }
    : null;
};

const canViewQualityIssue = (
  ctServer: string,
  shouldUseRouter: boolean,
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  return user.permissions.qualityIssue.canViewQualityIssue
    ? {
        key: "quality",
        icon: "qc-dashboard",
        label: t("account.quality"),
        shouldUseRouter,
        link: ctServer + "/quality-dashboard",
        dataTest: "quality",
      }
    : null;
};

const canViewAccountSetting = (
  umServer: string,
  shouldUseRouter: boolean,
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  return user.permissions.isLoginAsAgent
    ? null
    : {
        key: "setting",
        icon: "accountSettings",
        label: t("account.setting"),
        shouldUseRouter,
        link: umServer + "/users/settings/account",
        alternateLink: [umServer + "/users/settings/notifications"],
        dataTest: "user-setting",
      };
};

const canViewHelpCentre = (
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  return user.permissions.isLoginAsAgent
    ? null
    : {
        key: "help-centre",
        icon: "userGuide",
        label: t("nav.help-centre"),
        link: "https://agentofferings.iproperty.com.my/help-centre/",
        dataTest: "help-centre",
        needOpenIcon: true,
      };
};

const canViewAgentNet = (
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  const AGENT_NET_DASHBOARD_URL =
    process.env.REACT_APP_PG_AGENT_NET_DASHBOARD ||
    "https://agentnet.propertyguru.com.my/v2/dash";

  return user.capability.isAgentNetUser
    ? {
        key: "agent-net-link",
        icon: "agent-net-icon",
        label: t("nav.agentNet"),
        link: AGENT_NET_DASHBOARD_URL,
        dataTest: "agent-net-link",
        needOpenIcon: true,
      }
    : null;
};

const canViewLogout = (
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  return user.permissions.isLoginAsAgent
    ? null
    : {
        key: "log-out",
        icon: "signOut",
        label: t("account.logOut"),
        link: generateLogoutUrl(),
        openInSameTab: true,
        dataTest: "user-logout",
        click: () => {
          const excludedKeys = [
            "x-geo-redemption-modal",
            "x-cross-listing-onboarding-modal",
            "x-purchase-button-tooltip",
            "x-pro-listing-insight-tooltip",
            "x-agentnet-listing-insight-tooltip",
            "x-agentnet-listing-performance-report-tooltip",
            "x-credit-expiry-onboarding",
            "x-specialist-self-serve-tooltip",
            "x-specialist-self-serve-recommendation-tooltip",
            // "x-introduce-buy-tooltip",
            // "x-subscription-renew-online-button-tooltip",
            "x-introduce-lead-management-tooltip",
            "x-lead-management-onboarding-tooltip",
            "x-repricing-onboarding-modal",
            "x-repricing-onboarding-tooltip",
            "x-lsot-listing-creation-onboarding-tooltip",
            "x-introduce-market-watch-tooltip",
            "x-introduce-market-watch-badge",
            "x-cross-listing-onboarding-tooltip",
            "x-listing-duration-change-tooltip",
          ];

          /**
           * get existing value
           **/
          const excludedKeysObject = excludedKeys.reduce(
            (obj: { [key: string]: string | null }, key) => {
              obj[key] = localStorage.getItem(key);
              return obj;
            },
            {}
          );

          localStorage.clear();

          /**
           * reapply value
           */
          Object.entries(excludedKeysObject).forEach(([key, value]) => {
            if (value) {
              setLocalStorageItem(key, value);
            }
          });
        },
      };
};

const canViewConsumerPage = (
  user: UserProps["viewer"],
  t: I18NProps["t"]
): MenuItemType | null => {
  return user.permissions.isLoginAsAgent
    ? null
    : {
        key: "open-consumer-page-my",
        label: t("account.openConsumer"),
        link:
          location.origin ||
          `${location.protocol}//${location.host}${location.port && ":"}${
            location.port
          }`,
        dataTest: "open-consumer-page",
        getCustomNode: (item) => (
          <LinkButton
            isGrey
            itemRouterOptions={item.routerOptions}
            itemLink={item.link || "#"}
            itemLabel={item.label}
            dataTest={item.dataTest}
            shouldUseRouter={item.shouldUseRouter}
          />
        ),
      };
};

export function generateAccountInfo(
  ctServer: string,
  umServer: string,
  headerMenu: MenuItemType[],
  shouldUseRouter: boolean,
  user: UserProps["viewer"],
  t: I18NProps["t"]
) {
  const menuList: Array<MenuItemType | null> = [
    {
      key: "agent",
      icon: "default-avatar",
      label: t("account.agent"),
      shouldUseRouter,
      link: umServer + "/users/user-profile",
      dataTest: "my-agent",
    },
    canViewSubscriptionsMenu(ctServer, shouldUseRouter, user, t),
    canViewPaymentHistory(ctServer, shouldUseRouter, user, t),
    canViewMyTeam(umServer, shouldUseRouter, user, t),
    canViewEnquirySettings(ctServer, shouldUseRouter, user, t),
    canViewAllocation(ctServer, shouldUseRouter, user, t),
    canViewQualityIssue(ctServer, shouldUseRouter, user, t),
    canViewAccountSetting(umServer, shouldUseRouter, user, t),
    canViewHelpCentre(user, t),
    canViewAgentNet(user, t),
    canViewLogout(user, t),
    canViewConsumerPage(user, t),
  ];
  const menus = mergeMenus(compact(menuList), headerMenu);
  return compact(menus);
}

interface HeaderProps {
  umServer: string;
  ctServer: string;
  withPermission: boolean;
  headerMenu?: MenuItemType[];
  trackMenuTap?: TrackMenuType;
  shouldUseRouter: boolean;
  inboxStatistic?: InboxStatistic;
  marketwatchStatistic?: MarketWatchStatistic;
  braze?: any;
}

const CustomSpecialistSelfServeOnboardingTooltip = (
  props: SpecialistSelfServeProps
) => {
  return <SpecialistSelfServeOnboardingTooltip {...props} />;
};

export function Header(props: HeaderProps) {
  const user = useUser();
  const { t } = useI18N();
  const { isMobilePlatform } = usePlatform();
  const config = useMarketConfig();
  const history = useHistory();
  const location = useLocation();
  const marketwatchStatisticUnread = props.marketwatchStatistic?.unread || 0;
  const [screenWidth, setScreenWidth] = React.useState(window.innerWidth);
  const [isDisplayIntroduceBuyTooltip, setIsDisplayIntroduceBuyTooltip] =
    useState<boolean>(false);
  const [
    isDisplaySpecialistSelfServeTooltip,
    setIsDisplaySpecialistSelfServeTooltip,
  ] = useState<boolean>(false);
  const [
    isDisplayIntroduceLeadManagementTooltip,
    setIsDisplayIntroduceLeadManagementTooltip,
  ] = useState<boolean>(false);
  const [
    isDisplayIntroduceMarketWatchTooltip,
    setIsDisplayIntroduceMarketWatchTooltip,
  ] = useState<boolean>(false);
  const [
    isDisplayIntroduceMarketWatchBadge,
    setIsDisplayIntroduceMarketWatchBadge,
  ] = useState<boolean>(false);
  const [
    displayAgentoolNotificationBadge,
    setDisplayAgentoolNotificationBadge,
  ] = useState<boolean>(false);
  const [
    marketwatchNotificationBadgeText,
    setMarketwatchNotificationBadgeText,
  ] = useState<string>("");
  const [isDisplayListingsV3Tooltip, setIsDisplayListingsV3Tooltip] =
    useState<boolean>(false);
  const [isDisplayListingsV3RedDot, setIsDisplayListingsV3RedDot] =
    useState<boolean>(false);
  const [
    isDisplayBrickzOnboardingTooltip,
    setIsDisplayBrickzOnboardingTooltip,
  ] = useState<boolean>(false);

  React.useEffect(() => {
    const onWindowResize = debounce(
      () => setScreenWidth(window.innerWidth),
      300
    );
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  const isLeadManagementEnabled =
    process.env.REACT_APP_FEATURE_LEAD_MANAGEMENT_V2 === "on";
  const isMarketWatchEnabled = moment().isAfter(
    process.env.REACT_APP_FEATURE_MARKET_WATCH_START
  );
  const isExistingUserForMarketWatch = useMemo(() => {
    let isExistingUser = false;
    if (user?.createdDate) {
      // if user created date is before market watch start date, then it is an existing user
      isExistingUser = moment(user.createdDate).isBefore(
        process.env.REACT_APP_FEATURE_MARKET_WATCH_START
      );
    }
    return isExistingUser;
  }, [user]);

  const leadsUrl = isLeadManagementEnabled ? "/v2/leads" : "/leads";
  const marketwatchUrl = "/market-watch";

  // hide "New" tag if LDM is not enabled or after LDM phase 1 ends
  const isHideLdmNewTag =
    !isLeadManagementEnabled ||
    moment().isAfter(
      process.env.REACT_APP_FEATURE_LEAD_MANAGEMENT_V2_PHASE_1_END_DATE
    );

  const isHideMarketWatchOnboarding =
    !isMarketWatchEnabled ||
    !isExistingUserForMarketWatch ||
    moment().isAfter(
      process.env.REACT_APP_FEATURE_MARKET_WATCH_ONBOARDING_END_DATE
    );
  const isHideMarketWatchBadge =
    !isMarketWatchEnabled ||
    !isExistingUserForMarketWatch ||
    moment().isAfter(
      process.env.REACT_APP_FEATURE_MARKET_WATCH_ONBOARDING_END_DATE
    );
  const isHideMarketWatchBetaTag =
    !isMarketWatchEnabled ||
    moment().isAfter(process.env.REACT_APP_FEATURE_MARKET_WATCH_BETA_END_DATE);

  useEffect(() => {
    setIsDisplaySpecialistSelfServeTooltip(
      !isLeadManagementEnabled &&
        !localStorage.getItem("x-specialist-self-serve-tooltip")
    );

    setIsDisplayIntroduceLeadManagementTooltip(
      !isHideLdmNewTag &&
        !localStorage.getItem("x-introduce-lead-management-tooltip")
    );

    setIsDisplayIntroduceMarketWatchTooltip(
      isExistingUserForMarketWatch &&
        !isHideMarketWatchOnboarding &&
        !localStorage.getItem("x-introduce-market-watch-tooltip")
    );

    setIsDisplayIntroduceMarketWatchBadge(
      isExistingUserForMarketWatch &&
        !isHideMarketWatchBadge &&
        !localStorage.getItem("x-introduce-market-watch-badge")
    );
  }, [isExistingUserForMarketWatch]);

  useEffect(() => {
    setMarketwatchNotificationBadgeText(
      marketwatchStatisticUnread >= 100
        ? "99+"
        : marketwatchStatisticUnread.toString()
    );
    setDisplayAgentoolNotificationBadge(
      isDisplayIntroduceMarketWatchBadge || marketwatchStatisticUnread > 0
    );
  }, [isDisplayIntroduceMarketWatchBadge, marketwatchStatisticUnread]);

  const isSpecialistSelfServiceEnabled = React.useMemo(() => {
    const demoUserIds =
      process.env.REACT_APP_FEATURE_AREA_CONDO_SPECIALIST_SELF_SERVE_DEMO_USER_IDS?.split(
        ","
      ) || [];
    const isCanAccessSpecialist =
      process.env.REACT_APP_FEATURE_AREA_CONDO_SPECIALIST_SELF_SERVE === "on" ||
      demoUserIds.includes(user?.id);

    return user?.permissions?.isAgent && isCanAccessSpecialist;
  }, [user]);

  const isListingsV3Enabled = React.useMemo(() => {
    return user.featureToggle.isListingManagementV3WebEnabled;
  }, [user]);

  const isAgentListingReportWebEnabled = React.useMemo(() => {
    return user.featureToggle.isAgentListingReportWebEnabled;
  }, [user]);

  const isAgentBrickzEntitled = useAgentEntitlement(
    user.featureToggle.isAgentBrickzEnabled ? user.id : null,
    ProductKey.BRICKZ,
    FeatureKey.ACCESS
  );

  const isMarketAnalyticsWebEnabled =
    user.featureToggle.isMarketAnalyticsWebEnabled;

  const isAgentBrickzEnabled = React.useMemo(() => {
    return user.featureToggle.isAgentBrickzEnabled && isAgentBrickzEntitled;
  }, [user, isAgentBrickzEntitled]);

  const isUAPEnabled = React.useMemo(() => {
    return user.featureToggle.isUnifiedAdProductWebEnabled;
  }, [user]);

  useEffect(() => {
    const isOnDashboard = /\/dashboard/.test(location.pathname);
    // desktop - only show the tooltip when on dashboard page
    const shouldShowTooltip = isTablet(screenWidth) ? true : isOnDashboard;
    setIsDisplayListingsV3Tooltip(
      shouldShowTooltip &&
        isListingsV3Enabled &&
        !localStorage.getItem("x-listings-v3-onboarding-tooltip")
    );

    setIsDisplayListingsV3RedDot(
      isListingsV3Enabled && !localStorage.getItem("x-listings-v3-red-dot")
    );
  }, [location.pathname, isListingsV3Enabled]);

  const onClickListingsV3OnboardingTooltip = () => {
    setIsDisplayListingsV3Tooltip(false);
    localStorage.setItem("x-listings-v3-onboarding-tooltip", "1");
  };

  const onClickListingsV3RedDot = () => {
    setIsDisplayListingsV3RedDot(false);
    localStorage.setItem("x-listings-v3-red-dot", "1");
  };

  useEffect(() => {
    setIsDisplayBrickzOnboardingTooltip(
      isAgentBrickzEnabled &&
        !isDisplayListingsV3Tooltip &&
        !localStorage.getItem(BRICKZ_ONBOARDING_TOOLTIP_STORAGE_KEY)
    );
  }, [isAgentBrickzEnabled, isDisplayListingsV3Tooltip]);

  const onDismissBrickzOnboardingTooltip = () => {
    setIsDisplayBrickzOnboardingTooltip(false);
    localStorage.setItem(BRICKZ_ONBOARDING_TOOLTIP_STORAGE_KEY, "1");
  };

  function renderLogo() {
    return (
      <LogoWrapper config={config} screenWidth={screenWidth}>
        {props.shouldUseRouter ? (
          <Link
            to={props.ctServer + "/"}
            dangerouslySetInnerHTML={{
              __html: isMobile(screenWidth) ? mobileLogo : logo,
            }}
            title={t("title")}
          />
        ) : (
          <a
            href={props.ctServer + "/"}
            dangerouslySetInnerHTML={{
              __html: isMobile(screenWidth) ? mobileLogo : logo,
            }}
            title={t("title")}
          />
        )}
      </LogoWrapper>
    );
  }

  function renderPermissionHeader() {
    const { umServer, ctServer, trackMenuTap, braze } = props;

    return (
      <ThemeProvider theme={theme}>
        <HeaderWrapper withBottomBorder={false} hide={isMobilePlatform}>
          <GlobalStyle />
          <LoginAsAgentHint />
          {renderLogo()}
          <NavigationMenu
            umServer={umServer}
            ctServer={ctServer}
            menus={generateMenus(user)}
            screenWidth={screenWidth}
            trackMenuTap={trackMenuTap}
            displayNotificationBadge={
              displayAgentoolNotificationBadge || isDisplayListingsV3RedDot
            }
            displayMarketWatchTooltip={isDisplayIntroduceMarketWatchTooltip}
          />
          <AccountMenu
            ctServer={ctServer}
            braze={braze}
            trackMenuTap={trackMenuTap}
            screenWidth={screenWidth}
            menus={generateAccountInfo(
              props.ctServer,
              props.umServer,
              props.headerMenu!,
              props.shouldUseRouter,
              user,
              t
            )}
          />
        </HeaderWrapper>
      </ThemeProvider>
    );
  }

  const onConfirmSpecialistSelfServeTooltip = () => {
    setIsDisplaySpecialistSelfServeTooltip(false);
    localStorage.setItem("x-specialist-self-serve-tooltip", "1");
  };

  const listingsMenu = (
    shouldUseRouter: boolean,
    ctServer: string
  ): MenuItemType => {
    let specialistSelfServe: Partial<MenuItemType> = {};
    let specialistSelfServeSubMenu: MenuItemType | null = null;

    if (isSpecialistSelfServiceEnabled) {
      if (isDisplaySpecialistSelfServeTooltip) {
        specialistSelfServe = {
          tooltipPanelLabel: (isDrawerOpen) =>
            CustomSpecialistSelfServeOnboardingTooltip({
              isDrawerOpen,
              onConfirmSpecialistSelfServeTooltip,
              screenWidth,
            }),
          onTitleClick: !isDesktop(screenWidth)
            ? onConfirmSpecialistSelfServeTooltip
            : undefined,
        };
      } else {
        specialistSelfServe = {
          getCustomTagNode: !isLeadManagementEnabled && (
            <MenuTag pattern={1}>NEW</MenuTag>
          ),
        };
      }

      specialistSelfServeSubMenu = {
        key: "areaCondoSpecialistSelfServe",
        label: (
          <LabelWrap>
            Area & Condo Specialist{" "}
            {!isLeadManagementEnabled && <MenuTag pattern={2}>NEW</MenuTag>}
          </LabelWrap>
        ),
        shouldUseRouter,
        link: ctServer + "/area-condo-specialist",
        dataTest: "areaCondoSpecialist",
      };
    }

    let listingsV3: Partial<MenuItemType> = {};
    if (isDisplayListingsV3Tooltip || isDisplayListingsV3RedDot) {
      listingsV3 = {
        tooltipPanelLabel: (isDrawerOpen) => {
          if (isDisplayListingsV3Tooltip) {
            return (
              <StaticTooltip
                hide={isTablet(screenWidth) && !isDrawerOpen}
                position={Position.BOTTOM}
                customCss={
                  isTablet(screenWidth) && isDrawerOpen
                    ? {
                        arrow: {
                          left: 15,
                        },
                      }
                    : undefined
                }
                content={
                  <TooltipContentWrapper>
                    <div className={"title"}>
                      Introducing New Listing Management
                    </div>
                    <div className={"content"}>
                      Create and manage your listings all in one place
                    </div>
                  </TooltipContentWrapper>
                }
                onDismiss={onClickListingsV3OnboardingTooltip}
              >
                {t("nav.listings")}
              </StaticTooltip>
            );
          }

          return t("nav.listings");
        },
        getCustomTagNode:
          !isTablet(screenWidth) && isDisplayListingsV3RedDot ? (
            <NotificationBadge
              dot={true}
              color={"#C20000"}
              offset={[10, -20]}
            />
          ) : null,
      };
    }

    const listingsUrl = isListingsV3Enabled ? "/v2/listings" : "/listings";

    return {
      key: "listings",
      label: t("nav.listings"),
      link: "",
      dataTest: "listings-link",
      shouldUseRouter,
      ...specialistSelfServe,
      ...listingsV3,
      subMenus: [
        {
          key: "listings-management",
          label: t("nav.listingsManagement"),
          link: ctServer + listingsUrl,
          dataTest: "listings-management-link",
          shouldUseRouter: !isListingsV3Enabled, // should not use react router for ULM redirection
          routerOptions: {
            pathname: ctServer + listingsUrl,
            state: { reset: true },
          },
          onTitleClick: () => {
            if (isDisplayListingsV3Tooltip) {
              onClickListingsV3OnboardingTooltip();
            }

            if (isDisplayListingsV3RedDot) {
              onClickListingsV3RedDot();
            }
          },
        },
        !isListingsV3Enabled &&
        user.permissions.reAdvertiseScheduler.canViewReAdvertiseScheduler
          ? {
              key: "scheduled-manager",
              label: t("nav.scheduledManager"),
              link: ctServer + "/scheduled-manager",
              dataTest: "scheduled-manager-link",
              shouldUseRouter,
            }
          : null,
        user.permissions.viewWatermarkMenu &&
        process.env.REACT_APP_FEATURE_WATERMARK !== "off"
          ? {
              key: "watermark",
              label: t("nav.watermark"),
              shouldUseRouter,
              link: ctServer + "/watermark",
              dataTest: "watermark",
            }
          : null,
        specialistSelfServeSubMenu,
      ],
    };
  };

  const dashboardMenu = (
    shouldUseRouter: boolean,
    ctServer: string
  ): MenuItemType | null => {
    return user.permissions.dashboard.canViewDashboard
      ? {
          key: "dashboard",
          label: t("nav.dashboard"),
          link: ctServer + "/dashboard",
          dataTest: "dashboard-link",
          shouldUseRouter,
          routerOptions: {
            pathname: ctServer + "/dashboard",
            state: { reset: true },
          },
        }
      : null;
  };

  const insightsMenu = (
    shouldUseRouter: boolean,
    ctServer: string,
    trackMenuTap?: TrackMenuType
  ): MenuItemType => {
    let brickzOnboarding: Partial<MenuItemType> = {};
    const agentListingReportUrl = isAgentListingReportWebEnabled
      ? "/listing-overview"
      : "/reportings/performance";
    if (isDisplayBrickzOnboardingTooltip) {
      brickzOnboarding = {
        tooltipPanelLabel: (isDrawerOpen) => (
          <StaticTooltip
            hide={isTablet(screenWidth) && !isDrawerOpen}
            position={Position.BOTTOM}
            customCss={
              isTablet(screenWidth) && isDrawerOpen
                ? { arrow: { left: 15 } }
                : undefined
            }
            content={
              <TooltipContentWrapper>
                <div className={"title"}>{t("brickzOnboarding.title")}</div>
                <div className={"content"}>{t("brickzOnboarding.body")}</div>
              </TooltipContentWrapper>
            }
            okText={t("brickzOnboarding.cta")}
            onOk={() => {
              onDismissBrickzOnboardingTooltip();
              window.location.href = ctServer + "/v2/brickz";
            }}
            onDismiss={onDismissBrickzOnboardingTooltip}
          >
            {t("nav.insights")}
          </StaticTooltip>
        ),
      };
    }

    return {
      key: "insights",
      label: t("nav.insights"),
      link: "",
      dataTest: "insights-link",
      shouldUseRouter,
      ...brickzOnboarding,
      subMenus: [
        isAgentBrickzEnabled
          ? {
              key: "brickz-v2",
              label: t("nav.brickz"),
              dataTest: "brickz-v2-link",
              link: ctServer + "/v2/brickz",
              shouldUseRouter: false,
              routerOptions: {
                pathname: ctServer + "/v2/brickz",
                state: { reset: true },
              },
              onTitleClick: () => {
                if (isDisplayBrickzOnboardingTooltip) {
                  onDismissBrickzOnboardingTooltip();
                }
              },
            }
          : null,
        {
          key: "reportings",
          label: t("nav.reportings"),
          link: ctServer + agentListingReportUrl,
          dataTest: "reportings-link",
          shouldUseRouter: !isAgentListingReportWebEnabled,
          routerOptions: {
            pathname: ctServer + agentListingReportUrl,
            state: { reset: true },
          },
        },
        isMarketAnalyticsWebEnabled &&
        process.env.REACT_APP_FEATURE_MARKET_ANALYTICS !== "off"
          ? {
              key: "market-analytics",
              label: t("nav.marketAnalytics"),
              link: ctServer + "/market-analytics",
              dataTest: "market-analytics-link",
              shouldUseRouter,
              hiddenOnMobile: true,
            }
          : null,
        user.capabilityPermission.canAccessIqAgent
          ? {
              key: "iq-agent",
              label: t("nav.iqAgent"),
              dataTest: "iq-agent-link",
              needOpenIcon: true,
              subMenusWithForm: {
                trackMenuTap: trackMenuTap,
              },
            }
          : null,
        !isAgentBrickzEnabled && user.capabilityPermission.canAccessBrickz
          ? {
              key: "brickz",
              label: t("nav.brickz"),
              dataTest: "brickz-link",
              needOpenIcon: true,
              subMenusWithForm: {
                trackMenuTap: trackMenuTap,
              },
            }
          : null,
        user.permissions.report.canViewSpecialistReport
          ? {
              key: "specialistReport",
              label: t("nav.specialistReport"),
              dataTest: "specialist-report-link",
              shouldUseRouter,
              link: ctServer + "/reportings/specialist",
            }
          : null,
      ],
    };
  };

  const adminToolsSubMenu = (
    shouldUseRouter: boolean,
    ctServer: string
  ): Array<MenuItemType | null> => {
    return [
      user?.permissions?.announcement?.canViewManageAnnouncement
        ? {
            key: "announcements",
            label: t("nav.announcements"),
            shouldUseRouter,
            link: ctServer + "/announcements",
            dataTest: "announcement",
          }
        : null,
      user.permissions.viewOrganisationMenu
        ? {
            key: "management",
            label: t("account.management"),
            shouldUseRouter,
            link: ctServer + "/agency-branch/list",
            dataTest: "account-management",
          }
        : null,
      user.permissions.viewEnquiryManagementMenu
        ? {
            key: "enquiryManagement",
            label: t("account.enquiryManagement"),
            shouldUseRouter,
            link: ctServer + "/enquiry-management/overview",
            dataTest: "enquiryManagement",
          }
        : null,
      user.permissions.specialist.canAccessMenu &&
      process.env.REACT_APP_FEATURE_AREA_CONDO_SPECIALIST === "on"
        ? {
            key: "areaCondoSpecialist",
            label: "Area & Condo Specialist",
            shouldUseRouter,
            link: ctServer + "/area-condo-specialist",
            dataTest: "areaCondoSpecialist",
          }
        : null,

      process.env.REACT_APP_FEATURE_PAYMENT_GATEWAY === "on" &&
      user.permissions.selfServe.canViewPaymentHistory
        ? {
            key: "payment-history",
            label: "Payment History",
            shouldUseRouter,
            link: ctServer + "/payment-history",
            dataTest: "payment-history",
          }
        : null,

      process.env.REACT_APP_FEATURE_DYNAMIC_PRICING === "on" &&
      user.permissions.dynamicPricing.canViewDynamicPricing
        ? {
            key: "dynamic-pricing",
            label: "Dynamic Pricing",
            shouldUseRouter,
            link: ctServer + "/dynamic-pricing",
            dataTest: "dynamic-pricing",
          }
        : null,
    ];
  };

  const adminToolsMenu = (
    shouldUseRouter: boolean,
    ctServer: string
  ): MenuItemType | null => {
    return user.permissions.isInternalStaff
      ? {
          key: "adminTools",
          label: t("nav.adminTools"),
          link: "",
          dataTest: "admin-tools",
          shouldUseRouter,
          subMenus: adminToolsSubMenu(shouldUseRouter, ctServer),
        }
      : null;
  };

  const leadsMenu = (
    shouldUseRouter: boolean,
    ctServer: string,
    onConfirmIntroduceLeadManagementTooltip: () => void
  ): MenuItemType | null => {
    return user.permissions.lead.canViewLead
      ? {
          key: "leads",
          label: t("nav.leads"),
          link: ctServer + leadsUrl,
          dataTest: "leads-link",
          shouldUseRouter,
          routerOptions: {
            pathname: ctServer + leadsUrl,
            state: { reset: true },
          },
          tooltipPanelLabel: isDisplayIntroduceLeadManagementTooltip
            ? (isDrawerOpen, onClick) => (
                <StaticTooltip
                  okText={"Explore"}
                  onOk={() => {
                    onConfirmIntroduceLeadManagementTooltip();
                    onClick();
                  }}
                  hide={isTablet(screenWidth) && !isDrawerOpen}
                  position={Position.BOTTOM}
                  customCss={
                    isTablet(screenWidth) && isDrawerOpen
                      ? {
                          arrow: {
                            left: 15,
                          },
                        }
                      : undefined
                  }
                  content={
                    <TooltipContentWrapper>
                      <div className={"title"}>Introducing Lead Management</div>
                      <div className={"content"}>
                        Manage all your leads in one place, and get access to
                        exclusive insights to help you prioritise.
                      </div>
                    </TooltipContentWrapper>
                  }
                >
                  {t("nav.leads")}
                </StaticTooltip>
              )
            : undefined,
          getCustomTagNode: !isHideLdmNewTag && (
            <MenuTag pattern={3}>NEW</MenuTag>
          ),
          click: () => onConfirmIntroduceLeadManagementTooltip(),
        }
      : null;
  };

  const agentoolsMenu = (
    shouldUseRouter: boolean,
    ctServer: string,
    onConfirmIntroduceMarketWatchTooltip: (pushToMarketWatch?: boolean) => void
  ): MenuItemType | null => {
    const onAgentoolTitleClick = () => {
      if (!isHideMarketWatchOnboarding && !isTablet(screenWidth)) {
        setIsDisplayIntroduceMarketWatchTooltip(false);
        localStorage.setItem("x-introduce-market-watch-tooltip", "1");
      }
    };

    const onMarketWatchTitleClick = () => {
      if (!isHideMarketWatchOnboarding) {
        setIsDisplayIntroduceMarketWatchTooltip(false);
        localStorage.setItem("x-introduce-market-watch-tooltip", "1");

        setIsDisplayIntroduceMarketWatchBadge(false);
        localStorage.setItem("x-introduce-market-watch-badge", "1");
      }
    };

    const marketWatchNavBarLabel = () => (
      <LabelWrap>
        {t("nav.marketWatch")}
        {marketwatchStatisticUnread > 0 && (
          <CounterTag>{marketwatchNotificationBadgeText}</CounterTag>
        )}
        {!isHideMarketWatchBetaTag && <BetaTag>Beta</BetaTag>}
      </LabelWrap>
    );

    const marketWatchTooltipPanelLabel = (
      isDrawerOpen: boolean,
      onClick: () => void
    ) => {
      return (
        <StaticTooltip
          okText={"Explore now"}
          onOk={() => {
            onConfirmIntroduceMarketWatchTooltip(true);
            onClick();
          }}
          onDismiss={() => {
            onConfirmIntroduceMarketWatchTooltip(false);
          }}
          hide={isTablet(screenWidth) && !isDrawerOpen}
          position={Position.BOTTOM}
          customCss={
            isTablet(screenWidth) && isDrawerOpen
              ? {
                  arrow: {
                    left: 15,
                  },
                  tooltipWrapper: {
                    width: "100%",
                  },
                }
              : undefined
          }
          content={
            <TooltipContentWrapper>
              <div className={"title"}>Introducing MarketWatch!</div>
              <div className={"content"}>
                Scan market movements and co-broke easily.
              </div>
            </TooltipContentWrapper>
          }
        >
          {isTablet(screenWidth)
            ? marketWatchNavBarLabel()
            : t("nav.agentools")}
        </StaticTooltip>
      );
    };

    return user.permissions.agentools.canViewAgentools
      ? {
          key: "search-properties",
          label: t("nav.agentools"),
          link: "",
          dataTest: "agentools-link",
          shouldUseRouter,
          onTitleClick: onAgentoolTitleClick,
          subMenus: [
            isMarketWatchEnabled
              ? {
                  key: "market-watch",
                  label: marketWatchNavBarLabel(),
                  link: ctServer + "/market-watch",
                  dataTest: "market-watch-link",
                  shouldUseRouter,
                  hiddenOnMobile: true,
                  onTitleClick: onMarketWatchTitleClick,
                  tooltipPanelLabel:
                    isTablet(screenWidth) &&
                    isDisplayIntroduceMarketWatchTooltip
                      ? marketWatchTooltipPanelLabel
                      : undefined,
                }
              : null,
            isUAPEnabled
              ? null
              : {
                  key: "search-property",
                  label: t("nav.searchProperty"),
                  link: ctServer + "/search-properties",
                  dataTest: "search-property",
                  shouldUseRouter,
                  routerOptions: {
                    pathname: ctServer + "/search-properties",
                    state: { reset: true },
                  },
                },
            user.permissions.propertyWanted.canViewPropertyWanted &&
            moment().isBefore(process.env.REACT_APP_FEATURE_SUNSET_HOMEOWNER)
              ? {
                  key: "property-wanted",
                  label: t("nav.propertyWanted"),
                  link: ctServer + "/property-wanted",
                  dataTest: "property-wanted-link",
                  shouldUseRouter,
                  routerOptions: {
                    pathname: ctServer + "/property-wanted",
                    state: { reset: true },
                  },
                }
              : null,
            user.permissions.fsboAlert.canViewFsboAlert &&
            moment().isBefore(process.env.REACT_APP_FEATURE_SUNSET_HOMEOWNER)
              ? {
                  key: "fsbo-alerts",
                  label: t("nav.fsbo"),
                  shouldUseRouter,
                  link: ctServer + "/fsbo-alerts",
                  dataTest: "fasbo-alerts-link",
                }
              : null,
          ],
          getCustomTagNode:
            !isTablet(screenWidth) && displayAgentoolNotificationBadge ? (
              <NotificationBadge
                dot={true}
                color={"#C20000"}
                offset={[-5, -20]}
              />
            ) : null,
          tooltipPanelLabel:
            !isTablet(screenWidth) && isDisplayIntroduceMarketWatchTooltip
              ? marketWatchTooltipPanelLabel
              : undefined,
        }
      : null;
  };

  const creditBrickzMenu = (
    shouldUseRouter: boolean,
    ctServer: string,
    onConfirmIntroduceBuyTooltip: () => void
  ): MenuItemType | null => {
    return user.permissions.selfServe.canAccessCreditBrickz &&
      process.env.REACT_APP_FEATURE_PAYMENT_GATEWAY === "on"
      ? {
          key: "credit-brickz",
          label: t("nav.buy"),
          link: ctServer + "/credit-brickz",
          dataTest: "credit-brickz-link",
          shouldUseRouter,
          routerOptions: {
            pathname: ctServer + "/credit-brickz",
            state: { reset: true },
          },
          tooltipPanelLabel:
            isDisplayIntroduceBuyTooltip && !isSpecialistSelfServiceEnabled
              ? (isDrawerOpen) => (
                  <StaticTooltip
                    okText={"Got it"}
                    onOk={() => onConfirmIntroduceBuyTooltip()}
                    hide={isTablet(screenWidth) && !isDrawerOpen}
                    position={Position.BOTTOM}
                    customCss={
                      isTablet(screenWidth) && isDrawerOpen
                        ? {
                            arrow: {
                              left: 15,
                            },
                          }
                        : undefined
                    }
                    content={
                      <TooltipContentWrapper>
                        <div className={"title"}>Introducing ‘Buy’</div>
                        <div className={"content"}>
                          Purchase credits or Brickz add-on at your own
                          convenience.
                        </div>
                      </TooltipContentWrapper>
                    }
                  >
                    {t("nav.buy")}
                  </StaticTooltip>
                )
              : undefined,
          click: () => onConfirmIntroduceBuyTooltip(),
        }
      : null;
  };

  function generateMenus(user: UserProps["viewer"]) {
    const { ctServer, headerMenu, shouldUseRouter, trackMenuTap } = props;
    const isOptedInLCv3 =
      process.env.REACT_APP_FEATURE_LISTING_CREATION_V3 === "on" &&
      user.isToggledLCv3;
    const isLCv3FullRollout = moment().isAfter(
      process.env.REACT_APP_FEATURE_LCV3_FULL_ROLLOUT
    );

    const onConfirmIntroduceBuyTooltip = () => {
      setIsDisplayIntroduceBuyTooltip(false);
      localStorage.setItem("x-introduce-buy-tooltip", "1");
    };

    const onConfirmIntroduceLeadManagementTooltip = () => {
      if (!isHideLdmNewTag) {
        setIsDisplayIntroduceLeadManagementTooltip(false);
        localStorage.setItem("x-introduce-lead-management-tooltip", "1");
      }
      history.push(ctServer + leadsUrl);
    };

    const onConfirmIntroduceMarketWatchTooltip = (
      pushToMarketWatch: boolean = true
    ) => {
      if (!isHideMarketWatchOnboarding) {
        setIsDisplayIntroduceMarketWatchTooltip(false);
        localStorage.setItem("x-introduce-market-watch-tooltip", "1");

        if (isTablet(screenWidth)) {
          setIsDisplayIntroduceMarketWatchBadge(false);
          localStorage.setItem("x-introduce-market-watch-badge", "1");
        }
      }
      if (pushToMarketWatch) history.push(ctServer + marketwatchUrl);
    };

    const renderCreateListingButton = () => {
      const redirectToLCv3 = isOptedInLCv3 || isLCv3FullRollout;
      const redirectToLCv2 = !redirectToLCv3;
      const lcV2Link = ctServer + "/add-listing";
      const lcV3Link = ctServer + "/v2/add-listing";

      return (
        <LinkButton
          itemRouterOptions={
            redirectToLCv2
              ? {
                  pathname: lcV2Link,
                  state: { reset: true },
                }
              : undefined
          }
          itemLink={redirectToLCv3 ? lcV3Link : lcV2Link}
          itemLabel={t("nav.addListing")}
          dataTest={"add-listing"}
          height={51}
          shouldUseRouter={redirectToLCv2}
          isPrimary
        />
      );
    };

    const menuList: Array<MenuItemType | null> = [
      dashboardMenu(shouldUseRouter, ctServer),
      listingsMenu(shouldUseRouter, ctServer),
      insightsMenu(shouldUseRouter, ctServer, trackMenuTap),
      adminToolsMenu(shouldUseRouter, ctServer),
      leadsMenu(
        shouldUseRouter,
        ctServer,
        onConfirmIntroduceLeadManagementTooltip
      ),
      agentoolsMenu(
        shouldUseRouter,
        ctServer,
        onConfirmIntroduceMarketWatchTooltip
      ),
      user.permissions.listing.canCreateListing
        ? {
            key: "add-listing",
            getCustomNode: renderCreateListingButton,
          }
        : null,
      creditBrickzMenu(shouldUseRouter, ctServer, onConfirmIntroduceBuyTooltip),
    ];
    return mergeMenus(compact(menuList), headerMenu);
  }

  function renderNoPermissionHeader() {
    return (
      <HeaderWrapper withBottomBorder hide={isMobilePlatform}>
        {renderLogo()}
      </HeaderWrapper>
    );
  }
  return (
    <>
      {props.withPermission
        ? renderPermissionHeader()
        : renderNoPermissionHeader()}
    </>
  );
}

export const mergeMenus = (
  menuList: MenuItemType[],
  propsMenu?: MenuItemType[]
) => {
  if (!propsMenu) {
    return menuList;
  }
  return menuList.map((item) => {
    const currentMenu = find(propsMenu, ["key", item.key]);
    return currentMenu ? { ...item, ...currentMenu } : item;
  });
};

export const LogoWrapper = styled.div<{
  config: MarketConfig;
  screenWidth: number;
}>`
  height: 55px;
  line-height: 55px;
  padding-left: ${({ screenWidth }) => {
    if (isDesktop(screenWidth)) return "0px";
    return isMobile(screenWidth) ? "56px" : "62px";
  }};
  border-bottom: 1px solid ${theme.bgGray};
  ${media.md`
    padding-left: 24px;
    text-align: left;
    border-bottom: none;
    svg {
      width: 166px;
    }
  `};
  svg {
    height: 25px;
    vertical-align: middle;
    .st2 {
      fill: ${theme.textColor};
    }
    .icon {
      ${({ config }) =>
        config &&
        css`
          fill: ${config.primaryColor};
        `};
    }
  }
`;

export const HeaderWrapper = styled.header<{
  withBottomBorder: boolean;
  hide: boolean;
}>`
  position: relative;
  font-size: ${(props) => props?.theme?.fontSize?.body};
  background-color: white;
  z-index: 1;
  transition: z-index 1s;
  ${({ hide }) => hide && `display: none`};
  ${({ withBottomBorder }) =>
    withBottomBorder && `border-bottom: 1px solid ${theme.bgGray}`};
  > .ant-menu-horizontal {
    height: 49px;
  }
`;

const GlobalStyle = createGlobalStyle`
  .popside-bar-show {
    position:fixed;
    overflow: hidden;
    ${HeaderWrapper} {
      z-index: 100;
    }
  }
  .header-menu-drawer .ant-drawer-body {
    padding: 0;
    position: relative;
  }

  .ant-menu-submenu-popup .ant-menu-vertical.ant-menu-sub{
    margin-left: -10px;
    margin-top: -3px;
    padding: 0 16px;
    .ant-menu-item {
      font-weight: 400;
      font-size: ${(props) => props?.theme?.fontSize?.body};
      color: ${(props) => props.theme.textColor};
      background: ${(props) => props.theme.white};
      margin: 0;
      padding: 16px 0;
      height: auto;
      line-height: 1.6rem;

      &:hover, a:hover {
        color: ${(props) => props.theme.menuHoverColor};
      }
      
      &.ant-menu-item-selected {
        color: ${(props) => props.theme.menuActiveColor};

        a {
          color: inherit;
        }

        &:hover, a:hover  {
          color: ${(props) => props.theme.menuHoverColor};
        }
      }

      :first-child {
        display: none;
      }
      :not(:last-child) {
        border-bottom: 1px solid ${(props) => props.theme.bgGray};
      }
    }
  }
`;

export default Header;
